import api from "../http";
import {useEffect, useRef, useState} from "react";
import DocumentInfo from "./DocumentInfo";
import DocumentAccess from "./DocumentAccess";
import {useStore} from "../contexts/AppContext";
import {useAuth} from "../contexts/AuthContext";
import CopyIcon from '@rsuite/icons/Copy';
import PinIcon from '@rsuite/icons/Pin';
import * as analytics from "../utils/Analytics";
import SearchIcon from "@rsuite/icons/Search";
import {useLocation, useNavigate} from "react-router-dom";
import DocumentHistory from "./DocumentHistory";
import {duplicatesModal, items} from "../utils/constants"
import {Button, Card, Divider, Drawer, message, Modal, Popconfirm, Space, Steps, Tabs, theme, Tag} from 'antd';
import {LoadingOutlined} from "@ant-design/icons";
import {checkDocumentDuplicates, checkDocumentPublic, isDuplicateCheckNeeded} from "../utils/documentChecks";
import {Link2} from "lucide-react";
import AccessRequestForm from "./AccessRequestForm";


const MainTableModal = ({open, handleClose}) => {
    const [step, setStep] = useState(0);
    const [validStep, setValidStep] = useState(false)
    const {
        row, setRow, mode, setMode, tab, setTab, documents, setDocuments, setDraftNewDocument,
        newRowLoad, setNewRoadLoad, initialRow, setInitialRow, changed, setChanged, setOpen, setRequestAccessData,
        setNeedAccess
    } = useStore()
    const {userInfo} = useAuth()
    const navigate = useNavigate()
    const location = useLocation();
    const isIamDocOwner = row.current_roles_json.some(user => user.profile_id === userInfo.profile_id && user.role_id === 1)
    const isIamViewer = row.current_roles_json.some(user => user.profile_id === userInfo.profile_id && user.role_id === 4)
    const isAccessDenied = row.current_roles_json.some(user => user.profile_id === userInfo.profile_id && user.role_id === 0)
    const formRef = useRef(null);
    const {token} = theme.useToken();
    const [duplicatesData, setDuplicatesData] = useState(duplicatesModal)


    const onChangeSteps = async (value) => {
        setValidStep(true)
        try {
            if (value === 1 && step === 0 && formRef.current) {
                await formRef.current.validateFields();
                const foundDuplicates = await checkDocumentDuplicates(row);
                if (foundDuplicates) {
                    setDuplicatesData((prevState => ({
                        ...prevState,
                        state: true,
                        found: foundDuplicates
                    })))
                }
                if (row?.document_url) {
                    await checkDocumentPublic(row.document_url);
                }
            }
            setStep(value);
        } catch (error) {
            if (error?.errorFields) {
                message.error("Please check that the form is filled out correctly");
            } else {
                message.error(error.message || "An error has occurred");
            }
        } finally {
            setValidStep(false)
        }
    };


    useEffect(() => {
        if (open && row?.id) {
            setInitialRow(row);
        }
        // eslint-disable-next-line
    }, [open, row?.id]);


    useEffect(() => {
        if (!initialRow || !row) {
            setChanged(false);
        } else {
            setChanged(JSON.stringify(initialRow) !== JSON.stringify(row));
        }
    }, [row, initialRow, setChanged]);


    const changeDocStatus = async (row) => {
        let responseData
        try {
            if (row.is_archived) {
                responseData = await api.put(`supabase/unarchive-doc/${row.id}`)
            }
            if (!row.is_archived) {
                responseData = await api.put(`supabase/archive-doc/${row.id}`)
            }
            if (responseData.status === 200 && responseData.data.status) {
                const filteredDoc = documents.filter(doc => doc.id !== row.id)
                setDocuments(filteredDoc)
            }
        } catch (error) {
            message.error(error);
        } finally {
            handleClose()
        }
    }

    const onCatchError = (e) => {
        const msg = e?.response?.data;
        if (typeof msg === 'string' && /Document is public! Close access to your Google Document/i.test(msg)) {
            analytics.sendEvent('doc_is_public_warning', {
                doc_id: row?.id || null,
                doc_url: row?.document_url || null
            });
        }
    }


    const changeRow = async () => {
        setNewRoadLoad(true)
        try {
            const accessChanged = JSON.stringify(initialRow?.current_roles_json || []) !== JSON.stringify(row.current_roles_json || []);
            const response = await api.put(`/supabase/my-documents/${row.id}`, row);

            if (response.status === 200) {
                if (response.data.selfAccessRemoved) {
                    setDocuments((prevState) =>
                        prevState.filter((document) => document.id !== row.id)
                    );
                    handleClose()
                    analytics.sendEvent('doc_modified', {doc_id: row.id, doc_url: row?.document_url || null});
                    analytics.sendEvent('doc_self_access_removed', {
                        doc_id: row.id,
                        doc_url: row?.document_url || null
                    });
                }

                if (response.data.newDocumentData) {
                    setDocuments((prevState) => [
                        response.data.newDocumentData,
                        ...prevState.filter((document) => document.id !== row.id)
                    ]);
                    if (row.department_id) {
                        localStorage.setItem("department_id", row.department_id)
                    }
                    handleClose()
                    analytics.sendEvent('doc_modified', {doc_id: row.id, doc_url: row?.document_url || null});
                    if (accessChanged) {
                        analytics.sendEvent('doc_access_modified', {
                            doc_id: row.id,
                            doc_url: row?.document_url || null
                        });
                    }
                }
            }
        } catch (e) {
            onCatchError(e)
        } finally {
            setNewRoadLoad(false)
        }
    }


    const createRow = async () => {
        setNewRoadLoad(true)
        try {
            const response = await api.post(`/supabase/my-documents/`, row);
            if (response.status === 200) {
                setDocuments((prevState => [response.data.documentData, ...prevState]))
                analytics.sendEvent('doc_created', {
                    doc_id: response.data?.id || null,
                    doc_url: row?.document_url || null
                });
                localStorage.removeItem("draftNewDocument")
                setDraftNewDocument(false)
                setStep(0);
                handleClose()
            }
        } catch (e) {
            onCatchError(e)
        } finally {
            setNewRoadLoad(false)
        }
    }

    const onCopyDocument = () => {
        const copyDocument = {
            id: '',
            title: row.title,
            document_type_id: row.document_type_id,

            countries_id_array: row.countries_id_array,
            adv_res_id_array: row.adv_res_id_array,
            department_id: row.department_id,
            current_roles_json: [
                {
                    profile_id: userInfo.profile_id,
                    email: userInfo.email,
                    role_id: 1
                }
            ],

            period_id: row.period_id,
            period_type_id: row.period_type_id,
            period_types: row.period_types,
            period_year: row.period_year,
            period_quarter: row.period_quarter,
            period_month: row.period_month,

            brand_id: row.brand_id,
            date_start: row.date_start,
            date_end: row.date_end,
            tags_array: row.tags_array,
            description: row.description,
            document_url: row.document_url,
            is_archived: false,
            created_at: null,
            updated_ad: null
        }
        setNeedAccess([])
        setRow(copyDocument)
        setMode("newDocument");
        setStep(0);
    }

    const onCloseDraftDocument = () => {
        localStorage.setItem("draftNewDocument", JSON.stringify(row))
        setDraftNewDocument(true)
        handleClose()
    }


    const onFullPageView = () => {
        setTab("info")
        localStorage.setItem("prevPage", location.pathname)
        setOpen(false);
        navigate(`/dashboard/document/${row.id}`)
    }

    const onConfirm = async () => {
        if (mode === "editDocument") {
            if (isDuplicateCheckNeeded(row, initialRow)) {
                const foundDuplicates = await checkDocumentDuplicates({
                    ...row,
                    exclude_document_id: row.id
                });
                if (foundDuplicates) {
                    setDuplicatesData((prevState => ({
                        ...prevState,
                        state: true,
                        found: foundDuplicates
                    })))
                }
            }

            if (row?.document_url && initialRow?.document_url !== row?.document_url) {
                await checkDocumentPublic(row.document_url);
            }
            await changeRow()
        } else {
            await createRow()
        }
        message.success('Changes applied');
    }

    const onCancel = () => {
        message.info('Changes cancelled');
    }

    const onDisabledConfirmButton = () => {
        if (mode === "newDocument" && step === 0) {
            return true
        } else if (mode === "editDocument") {
            return !changed
        } else {
            return false
        }
    }

    const onOpenAccessRequestModal = (id) => {
        return setRequestAccessData((prevPage => ({
            ...prevPage,
            access_accept_access_mode: 'request',
            access_form_modal: true,
            document_id: id
        })))
    }


    return (
        <>
            <Drawer
                mask={false}
                maskClosable={false}
                title={mode === "editDocument" ? "Edit Document" : "New Document"}
                width={700}
                onClose={handleClose}
                open={open}
                styles={{
                    body: {
                        paddingBottom: 80,
                    },
                }}
                extra={
                    <Space>
                        {mode === "editDocument" &&
                            <>
                                <Button icon={<SearchIcon/>} onClick={onFullPageView}>Full Page</Button>
                                <Button icon={<CopyIcon/>} onClick={onCopyDocument}>Copy</Button>
                            </>
                        }
                        {mode === "newDocument" &&
                            <Button
                                onClick={onCloseDraftDocument}
                                icon={<PinIcon/>}
                            />
                        }
                        {mode === "editDocument" && userInfo.email === row.current_roles_json.find(user => user.role_id === 1)?.email &&
                            <Popconfirm
                                title="Warning"
                                description={`Are you sure ${row.is_archived ? "unarchive" : "archive this document"}?`}
                                placement="topRight"
                                onConfirm={() => changeDocStatus(row)}
                                onCancel={onCancel}
                                okText="Yes"
                                cancelText="No"
                                color={token.colorBgContainer}
                            >
                                <Button danger>
                                    {row.is_archived ? "Unarchive" : "Archive"}
                                </Button>
                            </Popconfirm>
                        }
                        <Button onClick={handleClose} appearance="subtle">
                            Cancel
                        </Button>
                        <Popconfirm
                            title="Warning"
                            description={
                                !row.current_roles_json.some(user => user.role_id === 2)
                                && isIamDocOwner
                                && mode === "newDocument"
                                    ? "If you confirm this, you will become the responsible person for the document"
                                    : "Save changes?"
                            }
                            placement="topRight"
                            onConfirm={onConfirm}
                            onCancel={onCancel}
                            okText="Yes"
                            cancelText="No"
                            color={token.colorBgContainer}
                        >
                            <Button
                                danger
                                loading={newRowLoad}
                                disabled={onDisabledConfirmButton()}>
                                {mode === "editDocument" ? "Save Changes" : "Confirm"}
                            </Button>
                        </Popconfirm>
                    </Space>
                }
            >
                <p style={{fontSize: 12, color: 'gray'}}>{mode === "editDocument" ? `id: ${row.id}` : ""}</p>
                {mode === "newDocument" &&
                    <div>
                        <Steps
                            current={step}
                            status="wait"
                            onChange={onChangeSteps}
                            items={[
                                {
                                    title: 'Info',
                                    description: 'Click for return',
                                    status: 'process',
                                    icon: validStep ? <LoadingOutlined/> : null,
                                },
                                {
                                    title: 'Access',
                                    description: 'Click for next'
                                },

                            ]}
                        />
                        <Divider dashed/>
                        <>
                            {step === 0 &&
                                <DocumentInfo
                                    ref={formRef}
                                    row={row}
                                    setRow={setRow}
                                />
                            }
                            {step === 1 &&
                                <DocumentAccess
                                    row={row}
                                    setRow={setRow}
                                />
                            }
                        </>
                    </div>
                }
                {mode === "editDocument" &&
                    <>
                        <Tabs
                            items={items}
                            type="card"
                            activeKey={tab}
                            onChange={(key) => setTab(key)}
                        />
                        {tab === "info" &&
                            <DocumentInfo
                                ref={formRef}
                                isIamViewer={isIamViewer}
                                isAccessDenied={isAccessDenied}
                            />
                        }
                        {tab === "access" &&
                            <DocumentAccess
                                isIamViewer={isIamViewer}
                                isAccessDenied={isAccessDenied}
                            />
                        }
                        {tab === "history" &&
                            <DocumentHistory/>
                        }
                    </>
                }
            </Drawer>
            <Modal
                title="Dublicates"
                closable={{'aria-label': 'Custom Close Button'}}
                width={1000}
                open={duplicatesData.state}
                onCancel={() => setDuplicatesData(duplicatesModal)}
                onOk={() => setDuplicatesData(duplicatesModal)}
                footer={[
                    <Button key="cancel" onClick={() => setDuplicatesData(duplicatesModal)}>
                        Cancel
                    </Button>,
                    <Button key="approve" type="primary" onClick={() => setDuplicatesData(duplicatesModal)}>
                        Create Anyway
                    </Button>
                ]}
            >
                {duplicatesData.found.map((fn, index) => {
                    return (
                        <Card key={fn.id || index}>
                            <Tag color={'magenta'}>ID: {fn.id}</Tag>
                            <Tag color={'blue'}>title: {fn.title}</Tag>
                            {fn.owner &&
                                <Tag color={'lime'}>owner: {fn.owner}</Tag>
                            }
                            {fn.responsible &&
                                <Tag color={'orange'}>responsible: {fn.responsible}</Tag>
                            }
                            <>
                                <Button
                                    size="xs"
                                    icon={<Link2 size={16}/>}
                                    href={`${process.env.REACT_APP_FRONTEND_URL}/dashboard/document/${fn.id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                />
                                {!fn?.has_permission_to_read &&
                                    <Button
                                        style={{marginLeft: 10}}
                                        type="primary"
                                        onClick={() => onOpenAccessRequestModal(fn.id)}
                                    >
                                        Send request
                                    </Button>
                                }

                            </>
                        </Card>
                    );
                })}
            </Modal>
            <AccessRequestForm/>
        </>
    )
}

export default MainTableModal;